import { Building, ChevronDown, Search } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { projectService } from '../../services/projectService';
import { Project } from '../../types';

interface ProjectSelectorProps {
  value?: string;
  onChange: (projectId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  label?: string;
}

const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = 'Select a project...',
  required = false,
  label = 'Project',
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { error } = useToast();

  // Load projects
  useEffect(() => {
    const loadProjects = async () => {
      setLoading(true);
      try {
        const response = await projectService.getProjects({
          limit: 100, // Get more projects for selection
          // Don't filter by status - get all projects for selection
        });

        if (response.success && response.data) {
          const projects = response.data.projects || [];
          console.log('Loaded projects for selector:', projects.length, projects);
          setProjects(projects);
        } else {
          console.error('Failed to load projects:', response);
          error('Failed to load projects');
          setProjects([]);
        }
      } catch (err) {
        console.error('Failed to load projects:', err);
        error('Failed to load projects');
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [error]);

  const selectedProject = projects.find(project => project._id === value || project.id === value);

  const filteredProjects = projects.filter(
    project =>
      project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleProjectSelect = (project: Project) => {
    onChange(project._id || project.id || '');
    setIsOpen(false);
    setSearchTerm('');
  };

  const clearSelection = () => {
    onChange('');
    setIsOpen(false);
    setSearchTerm('');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isOpen && !target.closest('.project-selector')) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="project-selector">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        <Building className="h-4 w-4 inline mr-2" />
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      {/* Dropdown trigger */}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
          disabled={disabled || loading}
          className={`w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary bg-white text-left disabled:bg-gray-50 disabled:cursor-not-allowed transition-all duration-200 ${
            !selectedProject && required ? 'border-red-300 ring-2 ring-red-100' : 'border-gray-300'
          } ${isOpen ? 'ring-2 ring-brand-primary border-brand-primary shadow-lg' : 'hover:border-gray-400 hover:shadow-md'}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {selectedProject && (
                <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building className="h-4 w-4 text-brand-primary" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                {loading ? (
                  <span className="text-gray-500">Loading projects...</span>
                ) : selectedProject ? (
                  <div>
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {selectedProject.name}
                    </p>
                    {selectedProject.description && (
                      <p className="text-xs text-gray-500 truncate">
                        {selectedProject.description}
                      </p>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-500">{placeholder}</span>
                )}
              </div>
            </div>

            <ChevronDown
              className={`h-4 w-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </button>

        {/* Dropdown menu */}
        {isOpen && !disabled && (
          <div className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-80 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
            {/* Search */}
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search projects..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-brand-primary focus:border-brand-primary text-sm"
                />
              </div>
            </div>

            {/* Projects list */}
            <div className="max-h-60 overflow-y-auto">
              {/* Clear selection option */}
              {selectedProject && (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 text-gray-500 border-b border-gray-200 transition-colors"
                >
                  <span className="italic">Clear selection</span>
                </button>
              )}

              {loading ? (
                <div className="p-6 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary mx-auto mb-3"></div>
                  <p className="text-sm">Loading projects...</p>
                </div>
              ) : filteredProjects.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <Building className="h-8 w-8 mx-auto mb-3 text-gray-400" />
                  <p className="text-sm font-medium mb-1">
                    {searchTerm ? 'No projects found' : 'No projects available'}
                  </p>
                  <p className="text-xs text-gray-400">
                    {searchTerm
                      ? 'Try adjusting your search terms'
                      : 'Contact your administrator to create projects'}
                  </p>
                </div>
              ) : (
                filteredProjects.map(project => {
                  const isSelected = selectedProject?._id === project._id;

                  return (
                    <button
                      key={project._id}
                      type="button"
                      onClick={() => handleProjectSelect(project)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                        isSelected ? 'bg-brand-50 text-brand-700' : 'text-gray-900'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              isSelected ? 'bg-brand-200' : 'bg-gray-100'
                            }`}
                          >
                            <Building
                              className={`h-4 w-4 ${isSelected ? 'text-brand-primary' : 'text-gray-400'}`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{project.name}</p>
                            {project.description && (
                              <p className="text-xs text-gray-500 truncate">
                                {project.description}
                              </p>
                            )}
                            <div className="flex items-center space-x-2 mt-1">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  project.status === 'active'
                                    ? 'bg-green-100 text-green-800'
                                    : project.status === 'planning'
                                      ? 'bg-brand-100 text-brand-800'
                                      : project.status === 'on_hold'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {project.status}
                              </span>
                              {project.location?.city && (
                                <span className="text-xs text-gray-400">
                                  {project.location.city}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {isSelected && <span className="text-brand-primary text-sm">✓</span>}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Validation message */}
      {required && !selectedProject && (
        <p className="mt-1 text-sm text-red-600">Project selection is required</p>
      )}
    </div>
  );
};

export default ProjectSelector;
